import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  EmptyState,
  Skeleton,
  ErrorState,
} from '@/components/ui'
import { Gauge, Plus, Info } from 'lucide-react'
import { api, type BillingMeter } from '@/api/client'

export function AppMetersPage() {
  const { orgSlug, appId } = useParams()
  const [meters, setMeters] = useState<BillingMeter[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchMeters = async () => {
      if (!orgSlug || !appId) return
      try {
        setError(null)
        const response = await api.apps.listMeters(orgSlug, appId)
        setMeters(response.meters)
      } catch (err) {
        console.error('Failed to fetch meters:', err)
        setError(err instanceof Error ? err.message : 'Failed to load meters')
      } finally {
        setIsLoading(false)
      }
    }
    fetchMeters()
  }, [orgSlug, appId])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="py-6">
                <Skeleton className="h-10 w-10 rounded-lg mb-3" />
                <Skeleton className="h-5 w-32 mb-2" />
                <Skeleton className="h-4 w-48" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load meters"
        description={error}
        action={<Button onClick={() => window.location.reload()}>Try Again</Button>}
      />
    )
  }

  if (meters.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
          <Info className="h-4 w-4" />
          <span>Meters are defined in your app's code using the SDK. Deploy your app to sync meters.</span>
        </div>
        <EmptyState
          icon={Gauge}
          title="No meters defined"
          description="Define meters in your app's code to track billable usage."
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
        <Info className="h-4 w-4" />
        <span>Meters are defined in your app's code and synced automatically on deploy.</span>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {meters.map((meter) => (
          <Card key={meter.id}>
            <CardContent className="py-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--bg-elevated-2)] mb-3">
                    <Gauge className="h-5 w-5 text-[var(--text-secondary)]" />
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-[var(--text-primary)]">{meter.name}</h3>
                    <Badge variant={meter.is_active ? 'success' : 'default'}>
                      {meter.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <p className="text-sm text-[var(--text-muted)] mb-3">
                    {meter.description || 'No description'}
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge variant="default">{meter.meter_type}</Badge>
                    <Badge variant="default">{meter.aggregation}</Badge>
                    {meter.unit && <Badge variant="default">{meter.unit}</Badge>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
